// @ts-nocheck
import { useState, useCallback, useMemo, useEffect } from 'react';
import { EMPTY_OBJECT } from '../const/CommonConst';
import { getVisibleAttributes } from '../utils/conditionalRuleEvaluator';

/**
 * Custom hook to manage error dialog state and logic for mandatory attribute validation
 * This hook can be used independently at any component level (ModelSummary, EditModelDetails, Overview)
 *
 * @param {Object} params - Hook parameters
 * @param {Object} params.entityDetailsFormikForm - Formik form instance with values, errors, setErrors, setTouched
 * @param {Function} params.validateFilteredFormikErrors - Function to validate and get filtered errors
 * @param {Object} params.formDetails - Complete form details with attributes and sections structure
 * @param {Object} params.attributeDetails - Current attribute values from API
 * @param {Object} params.syncAttributes - Synced/updated attribute values
 * @param {Function} params.setShowErrorsOnSave - Resets the caller's "a save was attempted" flag
 * @returns {Object} - Error dialog state, validation methods, and dialog controls
 */
const useErrorDialog = ({
  entityDetailsFormikForm,
  validateFilteredFormikErrors,
  formDetails,
  attributeDetails = EMPTY_OBJECT,
  syncAttributes = EMPTY_OBJECT,
  setShowErrorsOnSave,
  fieldLabelOverrides = EMPTY_OBJECT,
}) => {
  // Combine dialog state into single object to prevent multiple re-renders
  const [dialogState, setDialogState] = useState({
    isOpen: false,
    data: {},
  });

  // Extract only the specific properties we need to avoid unnecessary re-renders
  const formikErrors = entityDetailsFormikForm?.errors || {};
  const formikValues = entityDetailsFormikForm?.values || {};
  const formikSetErrors = entityDetailsFormikForm?.setErrors;
  const formikSetTouched = entityDetailsFormikForm?.setTouched;

  // Build complete section list from formDetails
  const allSectionList = useMemo(() => {
    const sections = [];

    // Add basic details section if attributes exist at root level
    if (formDetails?.attributes && Array.isArray(formDetails.attributes)) {
      sections.push({
        section_name:
          fieldLabelOverrides?.sectionLabel || 'General Information',
        attributes: formDetails.attributes,
      });
    }

    // Add all other sections
    if (formDetails?.sections && Array.isArray(formDetails.sections)) {
      formDetails.sections.forEach((section) => {
        if (section?.attributes && Array.isArray(section.attributes)) {
          sections.push({
            section_name: section.section_name,
            attributes: section.attributes,
          });
        }
      });
    }

    return sections;
  }, [formDetails?.attributes, formDetails?.sections]);

  // Get all form values combined - memoize with stable dependencies
  const allFormValues = useMemo(() => {
    return {
      ...attributeDetails,
      ...syncAttributes,
      ...formikValues,
    };
  }, [attributeDetails, syncAttributes, formikValues]);

  // Calculate visible attributes considering conditional visibility
  const { visibleAttributeListIds } = useMemo(() => {
    const attributeMap = new Map();

    const collectVisible = (attributes = []) => {
      const { visibleAttributes } = getVisibleAttributes(
        attributes,
        allFormValues
      );

      visibleAttributes.forEach((attr) => {
        if (attr?.id) {
          attributeMap.set(attr.id, attr);
        }
      });
    };

    // Collect from basic details
    if (Array.isArray(formDetails?.attributes)) {
      collectVisible(formDetails.attributes);
    }

    // Collect from all sections
    formDetails?.sections?.forEach((section) => {
      if (Array.isArray(section?.attributes)) {
        collectVisible(section.attributes);
      }
    });

    return {
      visibleAttributeListIds: new Set(attributeMap.keys()),
    };
  }, [formDetails, allFormValues]);

  const openErrorDialog = useMemo(() => {
    return dialogState.isOpen && Object.keys(dialogState.data || {}).length > 0;
  }, [dialogState.isOpen, dialogState.data]);

  const errorDialogData = useMemo(() => dialogState.data, [dialogState.data]);

  const handleOpenErrorDialog = useCallback((validationData) => {
    const nextData = validationData || {};
    setDialogState((prev) => {
      if (
        prev.isOpen &&
        JSON.stringify(prev.data) === JSON.stringify(nextData)
      ) {
        return prev;
      }
      return { isOpen: true, data: nextData };
    });
  }, []);

  const handleCloseErrorDialog = useCallback(() => {
    setDialogState((prev) =>
      prev.isOpen ? { isOpen: false, data: {} } : prev
    );
    setShowErrorsOnSave(false);
  }, [setShowErrorsOnSave]);

  /**
   * Build error result from formik errors
   * @param {Object} errors - Formik validation errors
   * @returns {Object} - Structured errors by section
   */
  const buildErrorResult = useCallback(
    (errors) => {
      const result = {};

      for (const section of allSectionList) {
        const mapped = {};
        for (const attr of section.attributes) {
          // Only include error if the attribute is visible and has an error
          if (errors[attr?.id] && visibleAttributeListIds.has(attr?.id)) {
            const displayName =
              fieldLabelOverrides?.fieldLabels?.[attr?.name] || attr?.name;
            mapped[displayName] = errors[attr?.id];
          }
        }

        if (Object.keys(mapped).length > 0) {
          result[section?.section_name] = mapped;
        }
      }

      return result;
    },
    [allSectionList, visibleAttributeListIds, fieldLabelOverrides]
  );

  /**
   * Validate only currently visible attributes (used for saves within a tab)
   */
  const validateAndShowErrors = useCallback(async () => {
    const errors = await validateFilteredFormikErrors();
    if (Object.keys(errors).length > 0) {
      if (formikSetErrors) {
        formikSetErrors(errors);
      }
      if (formikSetTouched) {
        formikSetTouched(
          Object.fromEntries(Object.keys(errors).map((k) => [k, true]))
        );
      }

      const result = buildErrorResult(errors);
      handleOpenErrorDialog(result);
      return false;
    }
    return true;
  }, [
    validateFilteredFormikErrors,
    formikSetErrors,
    formikSetTouched,
    buildErrorResult,
    handleOpenErrorDialog,
  ]);

  // Auto-close dialog when all errors are resolved (for any dialog opening method)
  useEffect(() => {
    if (!dialogState.isOpen) {
      return;
    }

    if (Object.keys(formikErrors)?.length > 0) {
      const result = buildErrorResult(formikErrors);

      if (Object.keys(result)?.length > 0) {
        handleOpenErrorDialog(result);
      } else {
        setDialogState((prev) =>
          prev.isOpen ? { isOpen: false, data: {} } : prev
        );
        setShowErrorsOnSave(false);
      }
    } else {
      setDialogState((prev) =>
        prev.isOpen ? { isOpen: false, data: {} } : prev
      );
    }
  }, [
    dialogState.isOpen,
    formikErrors,
    handleOpenErrorDialog,
    buildErrorResult,
    setShowErrorsOnSave,
  ]);

  return {
    // Dialog state
    openErrorDialog,
    errorDialogData,
    allSectionList, // Computed section list for DraggableErrorSectionDialog

    // Validation methods
    validateAndShowErrors, // For save operations (validates visible fields only)
    buildErrorResult, // group raw fromik errors

    // Dialog controls
    handleCloseErrorDialog,
    closeErrorDialog: handleCloseErrorDialog,
  };
};

export default useErrorDialog;
